// for an explanation of why this is named "unstable": https://reactrouter.com/docs/en/v6/routers/history-router
import { createSearchParams, unstable_HistoryRouter as HistoryRouter, HistoryRouterProps } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { getBasePath } from './RouterUtils';

// export { createSearchParams } from 'react-router-dom';

export const history = createBrowserHistory();
/**
 * Query param to preserve other params once.
 * We can always be sad it was not implemented camel or kebab case.
 */
export const RETAIN_PARAMS_PARAM = 'retainparameters';

export const FRAME_STYLE_PARAM = 'Chrome';

/** Ways a folder can be displayed, in _addition_ to the Portal itself, which is assumed. */
enum PossibleFolderLayers {
  /** Showing folder in overlay layer. Seems only used for report-helper#openActionForTextReport as 'layer' */
  Overlay,
}

/**
 * Track ways/places a folder is currently visible, for update events, etc.
 * This should never be updated directly, but via actions which show/hide/navigate in the related ways.
 *
 * This was in PortalStore, but was moved here because
 * 1. It has to do where user is in the UI (like the router does)
 * 1. It avoids importing PortalStore places we should not.
 * 1. Nothing is subscribed to this state; it just asks for it imperatively
 */
const visibleFolders = new Map<PossibleFolderLayers, string[]>();

interface IRouterProps extends Omit<HistoryRouterProps, 'history'> {
  portalBaseUrl: string;
  isStudio?: boolean;
}

/**
 * Use a history router, so we can export the history object, and then subscribe to history changes directly,
 * instead of relying on React component-tree to pass react-router hooks outputs to stores via useEffects, etc.
 * Maybe someday the root of the app/navigation will be React, and we won't need this, but it seems far off.
 *
 * Copyright (c) 2022 Decisions All Rights Reserved
 */
export function Router(props: IRouterProps) {
  return <HistoryRouter {...props} basename={ensureBaseUrl(props.portalBaseUrl, props.isStudio)} history={history} />;
}

/**
 * A helper, but it relies on the history here, so it's exported from this file.
 * @param params to update
 * @param retainParameters false to preserve any prior params, true is default, but if explicitly true, will also override retainparameters params
 * @returns new search string
 */
export function getUpdatedSearchParams(
  params: { [key: string]: string | undefined | boolean },
  retainParameters = false
) {
  // retainparameters - used by EndFormWithMessageStep only... don't like adding edge cases right out the gate,
  // but it is what it is. DT-026301
  const currentParams = createSearchParams(history.location.search);
  const frameStyle = getFrameStyleParam(currentParams);
  // less and less confident retainParams ever being passed via the URL argument is a real scenario anymore, but leaving it for now.
  retainParameters = retainParameters || currentParams.get(RETAIN_PARAMS_PARAM)?.toLocaleLowerCase() === `true`;
  // retainparameters should work one time
  currentParams.delete(RETAIN_PARAMS_PARAM);

  const init = !retainParameters ? undefined : currentParams;
  let updatedParams = createSearchParams(init);

  Object.keys(params).forEach(key => {
    const value = params[key];
    if (typeof value === 'undefined' || value === null) {
      updatedParams.delete(key);
    } else {
      updatedParams.set(key, value.toString());
    }
  });

  // put frameStyle / Chrome back on the end if it was there
  if (frameStyle) {
    updatedParams = removeFrameStyleParam(updatedParams);
    updatedParams.set(FRAME_STYLE_PARAM, frameStyle);
  }

  return updatedParams.toString();
}

export function showFolderInLayer(folderId: string) {
  updateFolderForLayer(PossibleFolderLayers.Overlay, folderId);
}

export function closeFolderLayer(folderId: string) {
  updateFolderForLayer(PossibleFolderLayers.Overlay, folderId, true);
}

export function getVisibleFoldersInLayers() {
  return Array.from(visibleFolders.values()).flatMap(arr => arr);
}

/** This abstraction was added when it looked like opening folders in dialog was still a thing, but leaving it, just in case. */
function updateFolderForLayer(layer: PossibleFolderLayers, folderId: string, remove = false) {
  let values = visibleFolders.get(layer);

  if (!values) {
    values = [];
    visibleFolders.set(layer, values);
  }

  if (remove) {
    values = values.filter(v => v !== folderId);
  } else {
    values = [...values, folderId];
  }

  visibleFolders.set(layer, values);
}

//#region private helpers

function getFrameStyleParam(params: URLSearchParams) {
  return (
    params.get(FRAME_STYLE_PARAM) ||
    params.get(FRAME_STYLE_PARAM.toUpperCase()) ||
    params.get(FRAME_STYLE_PARAM.toLowerCase())
  );
}

function removeFrameStyleParam(params: URLSearchParams) {
  const keys = Array.from(params.keys());
  const key = keys.find(k => k.toLowerCase() === FRAME_STYLE_PARAM.toLowerCase());

  key && params.delete(key);

  return params;
}

/**
 * Ensure expected root URL parts are present, e.g. after login, etc.
 *
 * Only exported for Jest tests.
 *
 * @param baseUrl that should be ensured to be at the root of the path
 * @param isStudio `true` if studio
 */
function ensureBaseUrl(baseUrl: string, isStudio?: boolean) {
  const currentPath = history.location.pathname;
  // path is everything in the portal base URL past the host
  const baseUrlPath = getBasePath(baseUrl, isStudio);

  if (currentPath !== baseUrlPath) {
    // this replaces the 'incorrect' entry
    history.replace({
      pathname: baseUrlPath,
      search: history.location.search,
    });
  }

  return baseUrlPath;
}

//#endregion private helpers
